import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryFirstImageUrl,
    getItemSummaryLotNumber,
    getItemSummaryTitle,
    getItemSummaryUrl,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { addImageParams } from '@/utils/urls';

type Props = {
    itemId: number;
    lastItem?: boolean;
    onClick?: () => void;
};

const UpcomingItemLink = ({ itemId, lastItem = false, onClick }: Props) => {
    const itemUrl = useAppSelector((state) => getItemSummaryUrl(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));
    const firstPhoto = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const nextImageUrl = addImageParams({ url: firstPhoto, width: 375 });
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));

    return (
        <NextItemLink
            $lastItem={lastItem}
            onClick={onClick}
            route
            to={itemUrl}
        >
            <NextItemTitle>
                <FormattedMessage
                    id="next_lot_number"
                    values={{ lotNumber }}
                />
            </NextItemTitle>
            <NextItemImageContainer>
                <img
                    alt={title}
                    src={nextImageUrl}
                />
            </NextItemImageContainer>
        </NextItemLink>
    );
};

export default UpcomingItemLink;

const NextItemTitle = styled.div`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
    position: absolute;
    padding: 0 15px;
    border-radius: 13px;
    bottom: 15px;
    left: 15px;
    height: 26px;
    line-height: 26px;
    text-align: center;
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.shadowOverlay600};
    z-index: 3;
`;

const NextItemImageContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;

    img {
        max-height: 100%;
        max-width: 100%;
        object-fit: contain;
    }
`;

const lastItemLink = css`
    padding: 25px;

    ${NextItemTitle} {
        display: none;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const NextItemLink = styled(Link)<{ $lastItem?: boolean }>`
    cursor: pointer;
    display: flex;
    justify-content: center;
    position: relative;
    overflow: hidden;
    width: calc(100% / 3);
    padding: 25px 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        height: 125px;
        position: absolute;
        right: 0;
        top: 0;
        width: 125px;
        z-index: 4;
        padding: 0;
        background-color: ${({ theme }) => theme.colors.grey400};

        img {
            border: none;
            height: auto;
            width: 120px;
        }

        ${NextItemTitle} {
            left: auto;
            height: auto;
            bottom: 0;
            font-size: 14px;
            line-height: normal;
            padding: 5px;
        }
    }

    ${({ $lastItem }) => $lastItem && lastItemLink}
`;
